/**
 * Provenance-index walker.
 *
 * Given a list of cloned source paths plus their resolved commits, walk each
 * clone, read its `skaile.manifest.yaml` (or fall back to the agentskills.io
 * filename layout), compute per-file and composite sha256s, and build a
 * {@link ProvenanceIndex} keyed by `<publisher>/<kind>:<name>`.
 *
 * Manifest discovery is recursive: a `skaile.manifest.yaml` at the repo root
 * *and* under any subdirectory contributes assets (a curator repo carries one
 * manifest per upstream). A manifest (or asset entry) with a `source:` pointer
 * is **curated** — its bytes live in the referenced upstream, so the candidate's
 * `sourceUrl`/`commit` stamp the upstream and the hashes come from the author-
 * pinned `sha256` / granular `files[].sha256` rather than from local bytes.
 *
 * This is the source-side half of the canonical-identity resolution model: the
 * resolver compares these candidates against store-side candidates and detects
 * divergent content hashes for the same `(publisher, kind, name, version)`.
 */

import { createHash } from "node:crypto";
import { existsSync, readdirSync, readFileSync } from "node:fs";
import { dirname, join, relative } from "node:path";
import fg from "fast-glob";
import { bundleDepRefs, parseFrontmatter, scanDirectory } from "./manifest.js";
import { isValidAssetName, slugifyAssetName } from "./models.js";
import {
  type AssetEntry,
  decodeSkaileManifest,
  type ManifestFileEntry,
  type ManifestSource,
  SKAILE_MANIFEST_FILENAME,
} from "./publish-manifest.js";

/** One cloned source repo, pinned at a resolved commit. */
export interface SourceClone {
  localPath: string;
  sourceUrl: string;
  /** 40-char SHA. */
  commit: string;
  /** Optional tag context for the version waterfall (step 3). */
  tag?: string;
  /**
   * Optional curated overlay manifest FILE (the local store's flat
   * `~/.skaile/store/manifests/<slug>.yaml`, authored by `source manifest init`).
   * When set and the file exists, it is the AUTHORITATIVE inventory for this
   * clone — the walker reads it INSTEAD of scanning the clone's own manifests /
   * agentskills.io layout, so the curated publisher + asset set resolve. Byte
   * paths (`root`/`files`) still resolve against `localPath`. Mirrors the
   * discovery-side overlay so `add`/resolve agree with what `source add` indexed.
   */
  overlayManifestPath?: string;
}

/** One resolved candidate in the provenance index. */
export interface ProvenanceCandidate {
  publisher: string;
  kind: string;
  name: string;
  /** Canonical semver string (or `0.0.0-sha.<7char>`). */
  version: string;
  sourceUrl: string;
  commit: string;
  files: Array<{ path: string; sha256: string }>;
  /** Composite sha256 over sorted `<path>:<sha256>\n` rows. */
  sha256: string;
  /** Parsed content frontmatter (mcp-server defaults etc.). */
  metadata?: Record<string, unknown>;
  /** Canonical transitive dependency refs (`kind:name@<publisher>[#pin]`).
   *  Populated from the manifest's `assets[].dependencies` when present, else
   *  for bundles from the bundle manifest. `resolveAll` recurses into these so a
   *  bundle pulls its members (and nested bundles). */
  deps?: string[];

  // ── Store-listing metadata (manifest-wins-else-frontmatter; all optional) ──
  description?: string;
  keywords?: string[];
  category?: string;
  license?: string;
  homepage?: string;
}

export type ProvenanceIndex = Map<string, ProvenanceCandidate[]>;

function fileSha256(p: string): string {
  return createHash("sha256").update(readFileSync(p)).digest("hex");
}

/**
 * Composite hash: SHA-256 (bare 64-hex) over `localeCompare`-sorted
 * `<path>:<sha256>\n` rows. Byte-for-byte identical to the store's
 * `computeCompositeSha256` (publish-shared.ts) and the `InstallManifest.sha256`
 * rollup, so source-side and store-side digests are directly comparable in the
 * resolver's cross-source divergence probe.
 */
function compositeSha256(files: Array<{ path: string; sha256: string }>): string {
  const lines = files
    .slice()
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((f) => `${f.path}:${f.sha256}\n`)
    .join("");
  return createHash("sha256").update(lines).digest("hex");
}

/**
 * Canonical GitHub-org → publisher slug. The single lowercasing rule shared by
 * the resolve side (`buildProvenanceIndex` here) and the index side
 * (`resolveSourcePublisher` in repo-manager). GitHub orgs are case-insensitive,
 * so a cased remote (`Acme`) and a lowercase index ref must not split.
 */
export function normalizeGithubOrg(org: string): string {
  return org.toLowerCase();
}

function publisherFromGithubUrl(url: string): string | undefined {
  const m = url.match(/github\.com[/:]([^/]+)\/[^/]+/);
  return m ? normalizeGithubOrg(m[1]) : undefined;
}

function syntheticVersion(commit: string): string {
  return `0.0.0-sha.${commit.slice(0, 7)}`;
}

/** Agentskills.io filename layout: `<repo-root>/<kind-plural>/<name>/<KIND>.md`. */
const KIND_DIRS: Array<{ dir: string; kind: string; mdName: string }> = [
  { dir: "skills", kind: "skill", mdName: "SKILL.md" },
  { dir: "agents", kind: "agent", mdName: "AGENT.md" },
  { dir: "bundles", kind: "bundle", mdName: "BUNDLE.md" },
  { dir: "mcp-servers", kind: "mcp-server", mdName: "MCP.md" },
  { dir: "connectors", kind: "connector", mdName: "CONNECTOR.md" },
  { dir: "prompts", kind: "prompt", mdName: "PROMPT.md" },
  { dir: "contracts", kind: "contract", mdName: "CONTRACT.md" },
];

const MD_FILE_BY_KIND: Record<string, string> = Object.fromEntries(
  KIND_DIRS.map((k) => [k.kind, k.mdName]),
);

/**
 * Walk a list of source clones into a provenance index.
 *
 * @param clones - Cloned source repos pinned at resolved commits.
 * @returns Map of `<publisher>/<kind>:<name>` → candidates.
 * @throws On a SKILL.md name mismatch or a missing publisher for a non-GitHub source.
 * @docLink packages/core/workspace-config#build-provenance-index
 */
export async function buildProvenanceIndex(
  clones: SourceClone[],
  _opts?: { projectDir?: string },
): Promise<ProvenanceIndex> {
  const index: ProvenanceIndex = new Map();
  for (const clone of clones) await walkOne(clone, index);
  return index;
}

async function walkOne(clone: SourceClone, index: ProvenanceIndex): Promise<void> {
  // Curated store overlay wins: when present, it is the authoritative inventory
  // (the same replace-on-presence rule discovery applies), so resolution agrees
  // with the curated publisher/assets `source add` indexed.
  if (clone.overlayManifestPath && existsSync(clone.overlayManifestPath)) {
    if (await walkWithManifest(clone, clone.overlayManifestPath, index)) return;
    // Overlay present but declared no assets[] — fall through to the clone scan.
  }
  const manifestPaths = findManifestFiles(clone.localPath);
  if (manifestPaths.length === 0) {
    await walkFilenameConvention(clone, index);
    return;
  }
  let produced = false;
  for (const p of manifestPaths) {
    if (await walkWithManifest(clone, p, index)) produced = true;
  }
  // Manifest(s) present but none declared assets[]: fall back to the
  // agentskills.io filename convention (matches the single-manifest behaviour).
  if (!produced) await walkFilenameConvention(clone, index);
}

/**
 * Recursively find every `skaile.manifest.yaml` under `root`, skipping
 * `.git`/`node_modules` (same skip list as the rest of the walker).
 *
 * **Deliberately synchronous and unbounded**, unlike `manifest.ts walkDir`.
 * `root` is always `clone.localPath` — a clone under `getGlobalCacheDir()`,
 * which is container-local storage this package created itself. It is never the
 * rclone FUSE workspace mount, so the #454 hazard (a sync call parking the
 * event loop on an unresponsive mount) does not apply. Do **not** copy this
 * shape to a path that can reach the mount, and if a caller ever passes one
 * here, convert this to the bounded async `walkDir` instead of widening it.
 */
function findManifestFiles(root: string): string[] {
  const found: string[] = [];
  const walk = (dir: string): void => {
    if (!existsSync(dir)) return;
    for (const entry of readdirSync(dir, { withFileTypes: true })) {
      if (entry.name === ".git" || entry.name === "node_modules") continue;
      const abs = join(dir, entry.name);
      if (entry.isDirectory()) walk(abs);
      else if (entry.isFile() && entry.name === SKAILE_MANIFEST_FILENAME) found.push(abs);
    }
  };
  walk(root);
  return found;
}

/**
 * Walk one `skaile.manifest.yaml` into the index. Returns `true` when it
 * produced at least one candidate.
 *
 * First-party assets (no `source:`) read bytes from the local clone. Curated
 * assets (manifest- or asset-level `source:`) are pointers: the candidate's
 * `sourceUrl`/`commit` stamp the upstream and the hashes come from the author-
 * pinned `sha256` / granular `files[].sha256`.
 */
async function walkWithManifest(
  clone: SourceClone,
  manifestPath: string,
  index: ProvenanceIndex,
): Promise<boolean> {
  const { manifest } = decodeSkaileManifest(readFileSync(manifestPath, "utf8"));
  const repoRoot = clone.localPath;
  const manifestPublisher = manifest.publisher ?? publisherFromGithubUrl(clone.sourceUrl);
  const manifestVersion =
    manifest.version ??
    (clone.tag ? clone.tag.replace(/^v/, "") : undefined) ??
    syntheticVersion(clone.commit);

  const assets = manifest.assets ?? [];
  if (assets.length === 0) return false;

  for (const a of assets) {
    const assetPublisher = a.publisher ?? manifestPublisher;
    if (!assetPublisher) {
      throw new Error(
        `${clone.sourceUrl}: publisher must be declared in skaile.manifest.yaml ` +
          "(non-GitHub source URL — auto-derivation not available).",
      );
    }
    const curatedSource = a.source ?? manifest.source;
    const version = a.version ?? manifestVersion;
    // A third-party manifest may declare a human-styled name; slugify it to a
    // canonical identity so the ref it produces parses (no-op when canonical).
    const name = canonicalAssetName(a.name);

    const resolved = resolveAssetBytes(clone, repoRoot, a, curatedSource);
    const { files } = resolved;

    const candidate: ProvenanceCandidate = {
      publisher: assetPublisher,
      kind: a.kind,
      name,
      version,
      sourceUrl: resolved.sourceUrl,
      commit: resolved.commit,
      files,
      sha256: a.sha256 ?? compositeSha256(files),
      metadata: resolved.metadata,
      deps: a.dependencies ?? (await manifestDeps(repoRoot, files, a.kind)),
    };

    applyStoreListingMetadata(candidate, a, manifest, resolved.frontmatter);

    push(index, `${assetPublisher}/${a.kind}:${name}`, candidate);
  }
  return true;
}

/** Canonical asset name: returned as-is when already valid, slugified when a
 *  third-party source declared a human-styled name, or left untouched when it
 *  can't be slugified (downstream `parseAssetRef` then reports the full error). */
function canonicalAssetName(raw: string): string {
  if (isValidAssetName(raw)) return raw;
  try {
    return slugifyAssetName(raw);
  } catch {
    return raw;
  }
}

/** Resolved bytes + provenance for one asset entry. */
interface ResolvedAssetBytes {
  files: Array<{ path: string; sha256: string }>;
  sourceUrl: string;
  commit: string;
  metadata?: Record<string, unknown>;
  frontmatter?: Record<string, unknown>;
}

/**
 * Resolve an asset's files + provenance stamp. Curated assets (a `source:`
 * pointer) read author-pinned granular `files[]`/sha256 and stamp the upstream;
 * first-party assets read bytes from the local clone and additionally surface
 * metadata + content frontmatter.
 */
function resolveAssetBytes(
  clone: SourceClone,
  repoRoot: string,
  a: AssetEntry,
  curatedSource: ManifestSource | undefined,
): ResolvedAssetBytes {
  if (curatedSource) {
    // Pointer — bytes live upstream (never vendored). Use the author-pinned
    // granular files[]/sha256; stamp provenance to the upstream.
    return {
      files: granularFiles(a) ?? [],
      sourceUrl: curatedSource.url,
      commit: curatedSource.commit ?? clone.commit,
    };
  }
  // First-party — bytes from the local clone.
  const files = expandAssetFiles(repoRoot, a);
  verifyNameMatch(repoRoot, files, a.name, a.kind);
  return {
    files,
    sourceUrl: clone.sourceUrl,
    commit: clone.commit,
    metadata: readMetadata(repoRoot, files, a.kind),
    frontmatter: readContentFrontmatter(repoRoot, files, a.kind),
  };
}

/**
 * Fold store-listing metadata onto a candidate in place: manifest field wins,
 * else content frontmatter, with repo-level license/homepage as the per-asset
 * default.
 */
function applyStoreListingMetadata(
  candidate: ProvenanceCandidate,
  a: AssetEntry,
  manifest: { license?: string; homepage?: string },
  frontmatter: Record<string, unknown> | undefined,
): void {
  const fmStr = (k: string): string | undefined =>
    typeof frontmatter?.[k] === "string" ? (frontmatter[k] as string) : undefined;
  const fmArr = (k: string): string[] | undefined =>
    Array.isArray(frontmatter?.[k])
      ? (frontmatter[k] as unknown[]).filter((x): x is string => typeof x === "string")
      : undefined;
  const description = a.description ?? fmStr("description");
  const keywords = a.keywords ?? fmArr("keywords");
  const category = a.category ?? fmStr("category");
  const license = a.license ?? manifest.license;
  const homepage = a.homepage ?? manifest.homepage;
  if (description) candidate.description = description;
  if (keywords && keywords.length > 0) candidate.keywords = keywords;
  if (category) candidate.category = category;
  if (license) candidate.license = license;
  if (homepage) candidate.homepage = homepage;
}

/** The glob strings of an asset's `files:` (first-party form). */
function globFiles(a: AssetEntry): string[] | undefined {
  if (!Array.isArray(a.files)) return undefined;
  const strings = a.files.filter((f): f is string => typeof f === "string");
  return strings.length > 0 ? strings : undefined;
}

/** The granular `{path, sha256}` entries of an asset's `files:` (curated form). */
function granularFiles(a: AssetEntry): ManifestFileEntry[] | undefined {
  if (!Array.isArray(a.files)) return undefined;
  const granular = a.files.filter(
    (f): f is ManifestFileEntry => !!f && typeof f === "object" && "path" in f && "sha256" in f,
  );
  return granular.length > 0 ? granular : undefined;
}

function expandAssetFiles(
  repoRoot: string,
  a: AssetEntry,
): Array<{ path: string; sha256: string }> {
  const out: Array<{ path: string; sha256: string }> = [];
  if (a.root) {
    walkDirRecursive(join(repoRoot, a.root), repoRoot, out);
  }
  const globs = globFiles(a);
  if (globs) {
    const matches = fg.sync(globs, { cwd: repoRoot, onlyFiles: true, dot: false });
    for (const rel of matches) {
      out.push({ path: rel, sha256: fileSha256(join(repoRoot, rel)) });
    }
  }
  // dedupe by path
  const seen = new Set<string>();
  return out.filter((f) => (seen.has(f.path) ? false : (seen.add(f.path), true)));
}

function walkDirRecursive(
  abs: string,
  root: string,
  out: Array<{ path: string; sha256: string }>,
): void {
  if (!existsSync(abs)) return;
  for (const entry of readdirSync(abs, { withFileTypes: true })) {
    if (entry.name === ".git" || entry.name === "node_modules") continue;
    const childAbs = join(abs, entry.name);
    if (entry.isDirectory()) {
      walkDirRecursive(childAbs, root, out);
    } else if (entry.isFile()) {
      out.push({ path: relative(root, childAbs), sha256: fileSha256(childAbs) });
    }
  }
}

/** Kinds whose asset spans the manifest file's whole containing directory
 *  (SKILL.md + scripts, etc.). The rest are single-file assets (a `.bundle.yaml`,
 *  `.flow.yaml`, or `.prompt.md` standing alone). */
const DIR_SCOPED_KINDS = new Set([
  "skill",
  "agent",
  "contract",
  "mcp-server",
  "connector",
  "knowledge",
]);

async function walkFilenameConvention(clone: SourceClone, index: ProvenanceIndex): Promise<void> {
  const publisher = publisherFromGithubUrl(clone.sourceUrl);
  if (!publisher) {
    throw new Error(
      `${clone.sourceUrl}: publisher must be declared in skaile.manifest.yaml ` +
        "(non-GitHub source URL).",
    );
  }
  const version =
    (clone.tag ? clone.tag.replace(/^v/, "") : undefined) ?? syntheticVersion(clone.commit);

  // Reuse the catalog scanner so the provenance fallback discovers exactly the
  // assets `manage` lists — recursively, by manifest filename, anywhere in the
  // tree. The old top-level `<kind-plural>/<name>/` scan missed nested assets
  // (e.g. `flows/x/x.bundle.yaml`): the TUI listed them but `add` couldn't
  // resolve them. See _devlog/notes/2026-06-02-provenance-fallback-recursion.md.
  for (const e of await scanDirectory(clone.localPath, publisher)) {
    const manifestAbs = e.source;
    const files: Array<{ path: string; sha256: string }> = [];
    if (DIR_SCOPED_KINDS.has(e.kind)) {
      walkDirRecursive(dirname(manifestAbs), clone.localPath, files);
    } else {
      files.push({ path: relative(clone.localPath, manifestAbs), sha256: fileSha256(manifestAbs) });
    }
    const candidate: ProvenanceCandidate = {
      publisher,
      kind: e.kind,
      name: e.name,
      version,
      sourceUrl: clone.sourceUrl,
      commit: clone.commit,
      files,
      sha256: compositeSha256(files),
      metadata: readMetadata(clone.localPath, files, e.kind),
      deps: await manifestDeps(clone.localPath, files, e.kind),
    };
    push(index, `${publisher}/${e.kind}:${e.name}`, candidate);
  }
}

function verifyNameMatch(
  repoRoot: string,
  files: Array<{ path: string; sha256: string }>,
  expectedName: string,
  kind: string,
): void {
  const mdName = MD_FILE_BY_KIND[kind];
  if (!mdName) return;
  const md = files.find((f) => f.path.endsWith(mdName));
  if (!md) return;
  const { data } = parseFrontmatter(readFileSync(join(repoRoot, md.path), "utf8"));
  const declared = typeof data.name === "string" ? data.name : undefined;
  // Compare on the canonical (slugified) form so a manifest and its SKILL.md that
  // agree on a human-styled name ("Complex App" vs "complex-app") don't false-trip.
  if (declared && canonicalAssetName(declared) !== canonicalAssetName(expectedName)) {
    throw new Error(
      `name mismatch: ${mdName} says "${declared}", skaile.manifest.yaml says ` +
        `"${expectedName}" — index-time hard error.`,
    );
  }
}

function readMetadata(
  repoRoot: string,
  files: Array<{ path: string; sha256: string }>,
  kind: string,
): Record<string, unknown> | undefined {
  // For MCP servers, return parsed frontmatter so loadMcpServerDeclarations
  // gets command/args/env defaults.
  if (kind !== "mcp-server") return undefined;
  const mcpMd = files.find((f) => f.path.endsWith("MCP.md"));
  if (!mcpMd) return undefined;
  const { data } = parseFrontmatter(readFileSync(join(repoRoot, mcpMd.path), "utf8"));
  return data;
}

/** Read the asset's manifest-file frontmatter (SKILL.md, AGENT.md, …) so the
 *  walker can fall back to content `description`/`keywords` when the manifest
 *  omits the store-listing fields. Returns undefined when the kind has no
 *  manifest file or it is absent among `files`. */
function readContentFrontmatter(
  repoRoot: string,
  files: Array<{ path: string; sha256: string }>,
  kind: string,
): Record<string, unknown> | undefined {
  const mdName = MD_FILE_BY_KIND[kind];
  if (!mdName) return undefined;
  const md = files.find((f) => f.path.endsWith(mdName));
  if (!md) return undefined;
  try {
    return parseFrontmatter(readFileSync(join(repoRoot, md.path), "utf8")).data;
  } catch {
    return undefined;
  }
}

/** For a bundle or flow, locate its manifest among `files` and read its
 *  transitive dep refs (a flow's `requires:`, a bundle's `requires`/`dependencies`).
 *  Without this, installing a flow deploys only the `.flow.yaml` and never the
 *  contracts/skills/sub-flows it declares. Returns undefined for other kinds or
 *  when no manifest is present. */
async function manifestDeps(
  repoRoot: string,
  files: Array<{ path: string; sha256: string }>,
  kind: string,
): Promise<string[] | undefined> {
  const ext =
    kind === "bundle"
      ? [".bundle.yaml", "BUNDLE.md"]
      : kind === "flow"
        ? [".flow.yaml", ".flow.json"]
        : null;
  if (!ext) return undefined;
  const mf = files.find((f) => ext.some((e) => f.path.endsWith(e)));
  return mf ? await bundleDepRefs(join(repoRoot, mf.path)) : undefined;
}

function push(index: ProvenanceIndex, key: string, candidate: ProvenanceCandidate): void {
  const arr = index.get(key) ?? [];
  arr.push(candidate);
  index.set(key, arr);
}
